import { useEffect, useMemo, useRef, useState } from 'react';
import type { PhotoItem } from './api';
import { fetchPhotos, requestUploadUrl, uploadFileToS3 } from './api';
import type { UserProfile } from './auth';
import { getCurrentUser, handleRedirectCallback, signIn, signOut } from './auth';
import { config } from './config';

interface NoticeState {
  kind: 'info' | 'success' | 'error';
  message: string;
}

const DEFAULT_NOTICE: NoticeState | null = null;

type FileWithDirectory = File & { webkitRelativePath?: string };

const normalizeFolder = (input: string): string => input.replace(/^\/+/, '').replace(/\/+$/, '');

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, exponent);
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[exponent]}`;
};

const resolveRelativeFolder = (file: File): string => {
  const relativePath = (file as FileWithDirectory).webkitRelativePath || '';
  if (!relativePath) return '';
  const segments = relativePath.split('/');
  segments.pop();
  return segments.join('/');
};

const deriveFolderForFile = (file: File, baseFolder: string): string => {
  const normalizedBase = normalizeFolder(baseFolder);
  if (normalizedBase) return normalizedBase;
  return normalizeFolder(resolveRelativeFolder(file));
};

const extractTopLevelFolder = (file: File): string => {
  const relative = resolveRelativeFolder(file);
  if (!relative) return '';
  return normalizeFolder(relative.split('/')[0] || '');
};

function App(): JSX.Element {
  const [user, setUser] = useState<UserProfile | null>(() => getCurrentUser());
  const [authError, setAuthError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [notice, setNotice] = useState<NoticeState | null>(DEFAULT_NOTICE);
  const [files, setFiles] = useState<File[]>([]);
  const [folder, setFolder] = useState(config.defaultUploadFolder);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (fileInputRef.current) {
      fileInputRef.current.setAttribute('webkitdirectory', '');
      fileInputRef.current.setAttribute('mozdirectory', '');
      fileInputRef.current.setAttribute('directory', '');
    }
  }, []);

  useEffect(() => {
    async function bootstrapAuth(): Promise<void> {
      try {
        const profile = await handleRedirectCallback();
        if (profile) {
          setUser(profile);
        }
      } catch (error) {
        console.error('Auth callback error', error);
        setAuthError((error as Error).message);
      }
    }

    void bootstrapAuth();
  }, []);

  useEffect(() => {
    if (!user) return;
    void loadPhotos('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.sub]);

  const sortedPhotos = useMemo(() => {
    return [...photos].sort((a, b) => a.photoId.localeCompare(b.photoId)).slice(0, config.maxResults);
  }, [photos]);

  const selectionSummary = useMemo(() => {
    if (files.length === 0) {
      return null;
    }
    const totalBytes = files.reduce((sum, file) => sum + file.size, 0);
    const preview = files.slice(0, 3).map((file) => file.name);
    const remaining = files.length - preview.length;
    return { totalBytes, preview, remaining };
  }, [files]);

  const showNotice = (kind: NoticeState['kind'], message: string) => {
    setNotice({ kind, message });
    window.setTimeout(() => setNotice(DEFAULT_NOTICE), 4000);
  };

  const loadPhotos = async (search: string) => {
    if (!user) return;
    try {
      setLoadingPhotos(true);
      setAuthError(null);
      const items = await fetchPhotos(search.trim());
      setPhotos(items);
      if (items.length === 0) {
        showNotice('info', 'Aucune photo pour ce filtrage pour le moment.');
      }
    } catch (error) {
      console.error('fetchPhotos error', error);
      showNotice('error', (error as Error).message);
    } finally {
      setLoadingPhotos(false);
    }
  };

  const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void loadPhotos(query);
  };

  const handleUpload = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (files.length === 0) {
      showNotice('error', 'Sélectionnez au moins une photo à téléverser.');
      return;
    }

    try {
      setUploading(true);
      const baseFolder = folder.trim();
      const uploadedKeys: string[] = [];

      for (const file of files) {
        const targetFolder = deriveFolderForFile(file, baseFolder);
        const { uploadUrl, key } = await requestUploadUrl(file, targetFolder);
        await uploadFileToS3(uploadUrl, file);
        uploadedKeys.push(key);
      }

      const successMessage = files.length === 1
        ? `Photo envoyée (${files[0].name}). Le traitement peut prendre quelques secondes.`
        : `${files.length} photos envoyées. Le traitement peut prendre quelques secondes.`;
      showNotice('success', successMessage);
      setFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      void loadPhotos(query);
    } catch (error) {
      console.error('upload error', error);
      showNotice('error', (error as Error).message);
    } finally {
      setUploading(false);
    }
  };

  const handleSignIn = () => {
    setAuthError(null);
    void signIn();
  };

  const handleSignOut = () => {
    clearClientState();
    signOut();
  };

  const clearClientState = () => {
    setPhotos([]);
    setNotice(DEFAULT_NOTICE);
    setQuery('');
    setFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="app-shell">
      <header className="header">
        <div className="brand">
          <h1>Album AI</h1>
          <span>Serveur photo intelligent (S3 + Rekognition)</span>
        </div>
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          {user ? (
            <>
              <div style={{ textAlign: 'right' }}>
                <strong>{user.name || user.email}</strong>
                <div className="notice">Connecté</div>
              </div>
              <button className="secondary" type="button" onClick={handleSignOut}>
                Se déconnecter
              </button>
            </>
          ) : (
            <button className="primary" type="button" onClick={handleSignIn}>
              Se connecter
            </button>
          )}
        </div>
      </header>

      {authError && <div className="card" style={{ border: '1px solid rgba(248, 113, 113, 0.4)' }}>{authError}</div>}

      {!user && (
        <section className="card" style={{ textAlign: 'center' }}>
          <h2>Connectez-vous pour explorer l’album</h2>
          <p className="notice">
            Nous utilisons Cognito (fédération sociale) pour sécuriser l’accès et personnaliser les recommandations.
          </p>
          <button className="primary" type="button" onClick={handleSignIn}>
            Continuer avec Cognito
          </button>
        </section>
      )}

      {user && (
        <>
          <section className="card">
            <form className="row" onSubmit={handleSearch}>
              <input
                className="input"
                placeholder="Recherche par personne, tag ou nom de fichier"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
              <button className="primary" type="submit" disabled={loadingPhotos}>
                {loadingPhotos ? 'Recherche…' : 'Rechercher'}
              </button>
              <button
                className="secondary"
                type="button"
                disabled={loadingPhotos}
                onClick={() => {
                  setQuery('');
                  void loadPhotos('');
                }}
              >
                Réinitialiser
              </button>
            </form>
            <p className="notice">
              Jusqu’à {config.maxResults} résultats. Les uploads sont analysés par Rekognition et enrichis par les noms de dossiers.
            </p>
          </section>

          <section className="card">
            <h2>Téléverser des photos</h2>
            <form className="row" onSubmit={handleUpload}>
              <input
                className="input"
                type="file"
                accept="image/*"
                multiple
                ref={fileInputRef}
                onChange={(event) => {
                  const list = event.target.files;
                  if (!list) {
                    setFiles([]);
                    return;
                  }
                  const selection = Array.from(list).filter((candidate) => candidate.type.startsWith('image/'));
                  setFiles(selection);
                  if (!folder && selection.length > 0) {
                    const inferred = extractTopLevelFolder(selection[0]);
                    if (inferred) {
                      setFolder(inferred);
                    }
                  }
                }}
              />
              <input
                className="input"
                placeholder="Dossier (optionnel)"
                value={folder}
                onChange={(event) => setFolder(event.target.value)}
              />
              <button className="primary" type="submit" disabled={uploading}>
                {uploading ? 'Envoi en cours…' : 'Envoyer'}
              </button>
            </form>
            {selectionSummary && (
              <div className="notice">
                {files.length} fichier(s) sélectionné(s) • {formatBytes(selectionSummary.totalBytes)}
                <div>
                  {selectionSummary.preview.join(', ')}
                  {selectionSummary.remaining > 0 ? ` + ${selectionSummary.remaining} autre(s)` : ''}
                </div>
              </div>
            )}
            <p className="notice">
              Les dossiers définissent les premières étiquettes “people”. Sélectionnez un dossier complet ou plusieurs fichiers : la structure sera conservée.
            </p>
          </section>

          {notice && (
            <section className="card" style={{ borderLeft: `4px solid ${notice.kind === 'error' ? '#f87171' : notice.kind === 'success' ? '#34d399' : '#60a5fa'}` }}>
              {notice.message}
            </section>
          )}

          {sortedPhotos.length === 0 && !loadingPhotos ? (
            <div className="empty-state">
              <h3>Pas encore de photos à afficher.</h3>
              <p className="notice">Chargez une image ou modifiez votre filtre.</p>
            </div>
          ) : (
            <section className="card-grid">
              {sortedPhotos.map((photo) => (
                <article className="card" key={photo.photoId}>
                  <img src={photo.url} alt={photo.photoId} loading="lazy" />
                  <div>
                    <strong>{photo.photoId.split('/').pop()}</strong>
                    <p className="notice">{photo.photoId}</p>
                  </div>
                  {photo.people?.length > 0 && (
                    <div className="tag-strip">
                      {photo.people.map((person) => (
                        <span className="tag" key={`person-${photo.photoId}-${person}`}>
                          👤 {person}
                        </span>
                      ))}
                    </div>
                  )}
                  {photo.labels?.length > 0 && (
                    <div className="tag-strip">
                      {photo.labels.slice(0, 6).map((label) => (
                        <span className="tag" key={`label-${photo.photoId}-${label}`}>
                          🏷️ {label}
                        </span>
                      ))}
                    </div>
                  )}
                </article>
              ))}
            </section>
          )}
        </>
      )}
    </div>
  );
}

export default App;
